import React from "react";
import { 
  Box, 
  Grid, 
  Typography, 
  IconButton, 
  Container, 
  Stack, 
  Link, 
  Button, 
  Divider,
  useMediaQuery,
  useTheme
} from "@mui/material";
import InstagramIcon from "@mui/icons-material/Instagram";
import TwitterIcon from "@mui/icons-material/Twitter";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { motion } from "framer-motion";

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  
  const currentYear = new Date().getFullYear();
  
  // Quick links for the footer
  const quickLinks = [
    { title: "Privacy Policy", url: "/privacy-policy" },
    { title: "Terms of Service", url: "/terms" },
    { title: "FAQ", url: "/faq" },
    { title: "Contact Us", url: "mailto:hello@truechamp.app" }
  ];
  
  // Social media links
  const socialLinks = [
    { icon: <InstagramIcon />, label: "Instagram", url: "https://instagram.com/truechampapp" },
    { icon: <TwitterIcon />, label: "Twitter", url: "https://twitter.com/truechampapp" },
    { icon: <LinkedInIcon />, label: "LinkedIn", url: "https://linkedin.com/company/truechampapp" }
  ];
  
  // Fade-in animation for elements
  const fadeInUpVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        position: "relative",
        backgroundColor: "transparent",
        color: "white",
        pt: { xs: 8, md: 10 },
        pb: { xs: 6, md: 8 },
        overflow: "hidden",
      }}
    >
      {/* Background gradient */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "linear-gradient(to bottom, #24243F, #1A1A35)",
          zIndex: -1,
        }}
      />
      
      {/* Subtle background pattern */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          opacity: 0.03,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          zIndex: -1,
        }}
      />

      <Container maxWidth="lg">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={{
            hidden: {},
            visible: {
              transition: {
                staggerChildren: 0.1
              }
            }
          }}
        >
          <Grid container spacing={6}>
            {/* Left Column: Logo and About */}
            <Grid item xs={12} md={4}>
              <motion.div variants={fadeInUpVariants}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <img src="/logo.png" alt="TrueChamp Logo" style={{ height: "50px" }} />
                  <Typography variant="h5" sx={{ ml: 2, fontWeight: 700 }}>
                  </Typography>
                </Box>
              </motion.div>
              
              <motion.div variants={fadeInUpVariants}>
                <Typography variant="body2" sx={{ lineHeight: 1.8, color: "rgba(255, 255, 255, 0.8)", mb: 3 }}>
                  Building habits that transform lives. Our AI-powered streak tracking app helps you stay accountable,
                  challenge friends, and build consistency that lasts.
                </Typography>
              </motion.div>
              
            </Grid>

            {/* Middle Column: Quick Links */}
            <Grid item xs={12} sm={6} md={4}>
              <motion.div variants={fadeInUpVariants}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
                  Quick Links
                </Typography>
              </motion.div>
              
              <Box component="nav">
                <Stack spacing={1.5}>
                  {quickLinks.map((link, index) => (
                    <motion.div key={index} variants={fadeInUpVariants}>
                      <Link
                        href={link.url}
                        underline="hover"
                        sx={{
                          color: "rgba(255, 255, 255, 0.7)",
                          display: "block",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            color: "#9b5af2",
                            transform: "translateX(5px)",
                          },
                        }}
                      >
                        {link.title}
                      </Link>
                    </motion.div>
                  ))}
                </Stack>
              </Box>
              
              <motion.div variants={fadeInUpVariants}>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<EmailOutlinedIcon />}
                  href="mailto:hello@truechamp.app"
                  sx={{
                    mt: 4,
                    backgroundColor: "#9b5af2",
                    color: "white",
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "0.9rem",
                    boxShadow: "0 4px 12px rgba(155, 90, 242, 0.3)",
                    "&:hover": {
                      backgroundColor: "#8347d6",
                    },
                  }}
                >
                  Contact Us
                </Button>
              </motion.div>
            </Grid>

            {/* Right Column: Connect */}
            <Grid item xs={12} sm={6} md={4}>
              <motion.div variants={fadeInUpVariants}>
                <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
                  Connect With Us
                </Typography>
              </motion.div>
              
              <motion.div variants={fadeInUpVariants}>
                <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
                  {socialLinks.map((social, index) => (
                    <IconButton
                      key={index}
                      aria-label={social.label}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "white",
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: "#9b5af2",
                          transform: "translateY(-3px)",
                        },
                      }}
                    >
                      {social.icon}
                    </IconButton>
                  ))}
                </Stack>
              </motion.div>
              
              <motion.div variants={fadeInUpVariants}>
                <Box 
                  sx={{
                    backgroundColor: "rgba(155, 90, 242, 0.1)",
                    borderRadius: "12px",
                    p: 3,
                    border: "1px solid rgba(155, 90, 242, 0.2)",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1, color: "#9b5af2", fontWeight: 600 }}>
                    Join Our Beta Program
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.8)", mb: 2 }}>
                    Get early access to new features and help shape the future of TrueChamp.
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    href="https://testflight.apple.com/join/Nkn4cBdH"
                    target="_blank"
                    sx={{
                      backgroundColor: "#9b5af2",
                      color: "white",
                      borderRadius: "8px",
                      textTransform: "none",
                      boxShadow: "0 4px 15px rgba(155, 90, 242, 0.3)",
                      "&:hover": {
                        backgroundColor: "#8347d6",
                      },
                    }}
                  >
                    Join iOS Beta
                  </Button>
                </Box>
              </motion.div>
            </Grid>
          </Grid>
          
          <Divider sx={{ my: 5, backgroundColor: "rgba(255, 255, 255, 0.1)" }} />
          
          {/* Bottom Credits Section */}
          <Box 
            sx={{ 
              display: "flex", 
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "center", md: "flex-start" },
              justifyContent: "space-between",
            }}
          >
            <motion.div variants={fadeInUpVariants}>
              <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.6)" }}>
                © {currentYear} TrueChamp. All rights reserved.
              </Typography>
            </motion.div>
            
            <motion.div variants={fadeInUpVariants}>
              <Typography variant="caption" sx={{ color: "rgba(255, 255, 255, 0.6)", mt: { xs: 1, md: 0 } }}>
                Crafted with dedication by{" "}
                <Link
                  href="https://www.linkedin.com/in/nathen-fernandes-544105175/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    color: "rgba(255, 255, 255, 0.8)", 
                    textDecoration: "none", 
                    fontWeight: "bold",
                    transition: "color 0.2s ease",
                    "&:hover": {
                      color: "#9b5af2",
                    }
                  }}
                >
                  Nathen Fernandes
                </Link>{" "}
                &{" "}
                <Link
                  href="https://www.linkedin.com/in/rayman-huang-9736791a7/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    color: "rgba(255, 255, 255, 0.8)", 
                    textDecoration: "none", 
                    fontWeight: "bold",
                    transition: "color 0.2s ease",
                    "&:hover": {
                      color: "#9b5af2",
                    }
                  }}
                >
                  Rayman Huang
                </Link>
              </Typography>
            </motion.div>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default Footer;